import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { AnnouncementAction, AnnouncementGetter } from '@/store/announcement/announcement.module';
import { AnnouncementSchema } from '@/store/announcement/announcement.validator';
import { Announcement } from '@/store/announcement/announcement.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'AnnouncementFormComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    announcementId: {
      type: Number
    },
  },
  emits: [
    'deleted'
  ],
  setup: (props, context) => {
    const swal = useSweetAlert();
    const isEditMode = computed(() => !!props.announcementId);
    const title = computed(() => isEditMode.value ? 'แก้ไขข่าวประชาสัมพันธ์' : 'เพิ่มข่าวประชาสัมพันธ์');
    const store = useStore();
    const announcement = computed<Announcement>(() => store.getters[AnnouncementGetter.BY_ID](props.announcementId));
    const announcementForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      title?: string;
      content?: string;
      pined: boolean;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      content: '',
      pined: false,
    });
    const validator = computed(() => AnnouncementSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      title: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'title')),
      content: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'content')),
    });
    const isValid = computed(() => !errors.title && !errors.content);
    const isLoading = computed<boolean>(() => store.getters[AnnouncementGetter.STATUS] === 'init' || store.getters[AnnouncementGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.announcementId;
          formData.title = announcement.value?.title;
          formData.content = announcement.value?.content;
          formData.pined = announcement.value?.pined;
        }
        resolve();
      })
      .then(() => announcementForm.value?.show());
    };

    // hide modal
    const hide = () => {
      announcementForm.value?.hide();
    };

    const handleHidden = () => {
      if (!announcement.value) {
        context.emit('deleted');
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.content = '';
        formData.pined = false;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(AnnouncementAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(AnnouncementAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(AnnouncementAction.DELETE, { id: props.announcementId })
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      announcementForm,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      deleteClick,
      handleHidden,
    };
  },
});
